import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
} from '@angular/common/http';
import { catchError, throwError, BehaviorSubject, filter, take, switchMap, Observable } from 'rxjs';
import { inject } from '@angular/core';
import { AuthService, IS_REFRESH_TOKEN } from '../services/auth/auth.service';
import { Store } from '@ngrx/store';
import * as AuthActions from '../../store/auth/auth.actions';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
  null
);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const store = inject(Store);
  const accessToken = authService.getAccessToken();
  const refreshToken = authService.getRefreshToken();

  let authReq = req;

  // If it's a refresh request, use the refresh token from local storage
  if (req.context.get(IS_REFRESH_TOKEN)) {
    if (refreshToken) {
      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${refreshToken}`,
        },
      });
    }
  } else if (accessToken) {
    // Normal request with access token
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // If 401 and it's NOT a refresh request itself
      if (error.status === 401 && !req.context.get(IS_REFRESH_TOKEN)) {
        if (!authService.getRefreshToken()) {
          store.dispatch(AuthActions.logout());
          return throwError(() => error);
        }
        return handle401Error(req, next, authService, store);
      }

      // If 401 or 403 on a refresh request, logout (clears everything)
      if ((error.status === 401 || error.status === 403) && req.context.get(IS_REFRESH_TOKEN)) {
        store.dispatch(AuthActions.logout());
      }

      return throwError(() => error);
    })
  );
};

const handle401Error = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  store: Store
): Observable<HttpEvent<any>> => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response) => {
        isRefreshing = false;
        const newToken = response.body?.data?.[0]?.accessToken;
        refreshTokenSubject.next(newToken || null);

        return next(
          request.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          })
        );
      }),
      catchError((err) => {
        isRefreshing = false;
        store.dispatch(AuthActions.logout());
        return throwError(() => err);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        return next(
          request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          })
        );
      })
    );
  }
};
